import { useEffect, useRef, useState } from 'react';
import { useStore } from '../lib/store';

/**
 * Pull down to refresh, built rather than borrowed.
 *
 * Installed to a home screen the app runs standalone: there is no address bar
 * to reload from, and iOS gives a standalone page no pull-to-refresh of its
 * own. The gesture everybody reaches for first therefore did nothing at all,
 * which reads as the app being stuck rather than as the app not having the
 * feature.
 *
 * Deliberately narrow. It only arms at the very top of the page and only for a
 * finger travelling down, so it can never fight a scroll or a swipe — and it
 * does nothing on a screen that has scrolled, which is where an accidental pull
 * would otherwise land.
 */

/** How far to pull before letting go does anything. */
const THRESHOLD = 70;
/** Past that, it gets stiff, so the sheet cannot be dragged halfway down. */
const MAX = 110;

export function PullToRefresh() {
  const { sync } = useStore();
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef<number | null>(null);
  const armed = useRef(false);

  useEffect(() => {
    const onStart = (e: TouchEvent) => {
      // Only from a resting page top. Anywhere else this is a scroll.
      if (window.scrollY > 0 || busy || e.touches.length !== 1) {
        armed.current = false;
        return;
      }
      /*
       * Not while a sheet is up.
       *
       * A sheet locks the body scroll, so `scrollY` is zero underneath it and
       * this armed on every downward drag — including the one that dismisses
       * the sheet, which meant swiping a drawer away also kicked off a refresh.
       *
       * Any open sheet disables it rather than only the one under the finger:
       * a sheet is modal, the page behind it is not being read, and there is no
       * version of "pull the page down" that makes sense while one is open.
       */
      if (document.querySelector('.scrim')) {
        armed.current = false;
        return;
      }
      armed.current = true;
      start.current = e.touches[0]!.clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (!armed.current || start.current === null) return;
      const travelled = e.touches[0]!.clientY - start.current;
      if (travelled <= 0) {
        // They are scrolling up after all. Get out of the way.
        armed.current = false;
        setPull(0);
        return;
      }
      // Square-rooted past the threshold so the last stretch resists, which is
      // what tells a thumb it has gone far enough without a word on screen.
      const eased =
        travelled <= THRESHOLD
          ? travelled
          : THRESHOLD + Math.sqrt(travelled - THRESHOLD) * 6;
      setPull(Math.min(MAX, eased));
    };

    const onEnd = () => {
      if (!armed.current) return;
      armed.current = false;
      start.current = null;
      setPull((current) => {
        if (current >= THRESHOLD) {
          setBusy(true);
          void sync().finally(() => {
            setBusy(false);
            setPull(0);
          });
          // Held open while it works, so the spinner has somewhere to be.
          return THRESHOLD;
        }
        return 0;
      });
    };

    // Passive: this never calls preventDefault. The page is already pinned at
    // the top by overscroll-behavior, so there is nothing to cancel — and a
    // non-passive listener on every touch would cost more than the feature.
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [sync, busy]);

  if (pull <= 0 && !busy) return null;

  const ready = pull >= THRESHOLD;
  /** How far towards doing something, 0 to 1. */
  const charge = Math.min(1, pull / THRESHOLD);

  /*
   * The ring geometry, in the SVG's own units. A dash the length of the whole
   * circumference, offset back by the charge, so the stroke draws itself round
   * as the finger travels — the gesture's progress made visible rather than
   * described after the fact by an arrow flipping over.
   */
  const R = 16;
  const CIRCUMFERENCE = 2 * Math.PI * R;

  return (
    <div
      className={`ptr${ready ? ' ready' : ''}${busy ? ' busy' : ''}`}
      style={{
        transform: `translate(-50%, ${pull}px) scale(${0.7 + charge * 0.3})`,
        opacity: Math.min(1, pull / 28),
      }}
      aria-hidden="true"
    >
      {busy ? (
        <span className="spinner" />
      ) : (
        <svg viewBox="0 0 40 40" width="40" height="40" fill="none">
          <circle className="ptr-track" cx="20" cy="20" r={R} strokeWidth="2.5" />
          <circle
            className="ptr-charge"
            cx="20"
            cy="20"
            r={R}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - charge)}
            // Starts at twelve o'clock and fills clockwise, which is the
            // direction the thumb is travelling.
            transform="rotate(-90 20 20)"
          />
          {/* Turns over the whole pull rather than snapping at the threshold,
              so the arrow is pointing back up at exactly the moment letting go
              starts to mean something. */}
          <path
            d="M20 13v14M14 21l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transformOrigin: 'center',
              transform: `rotate(${charge * 180}deg) scale(0.62)`,
            }}
          />
        </svg>
      )}
    </div>
  );
}
